import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check, MoreHorizontal, Trash2 } from '@/components/ui/icons';
import { PALETTE } from '@shared/constants';
import { useDb } from '@/lib/store';
import { createTag, deleteTag, updateTag } from '@/lib/actions';
import { rgba } from '@/lib/color';
import { MenuSearch } from '@/components/ui/Menu';
import { Popover } from '@/components/ui/Popover';

export function TagChip({ tagId, onRemove, small }: { tagId: string; onRemove?: () => void; small?: boolean }) {
  const tag = useDb((db) => db.tags[tagId]);
  if (!tag) return null;
  return (
    <span className={clsx('tag-chip', { small })} style={{ background: rgba(tag.color, 0.22), color: tag.color, borderColor: rgba(tag.color, 0.35) }}>
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        // a span, not a button: chips are often rendered inside button triggers
        <span
          role="button"
          tabIndex={-1}
          aria-label="Remove tag"
          className="tag-chip-x"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </span>
      )}
    </span>
  );
}

export function TagMenu({ spaceId, selected, onChange }: { spaceId: string; selected: string[]; onChange: (ids: string[]) => void }) {
  const tags = useDb((db) => db.tags);
  const [q, setQ] = useState('');
  const spaceTags = useMemo(
    () => Object.values(tags).filter((t) => t.spaceId === spaceId && (!q || t.name.includes(q.trim().toLowerCase()))).sort((a, b) => a.name.localeCompare(b.name)),
    [tags, spaceId, q],
  );
  const exact = spaceTags.some((t) => t.name === q.trim().toLowerCase());
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const create = () => {
    if (!q.trim() || exact) return;
    const t = createTag(spaceId, q, PALETTE[Math.floor(Math.random() * PALETTE.length)]);
    onChange([...selected, t.id]);
    setQ('');
  };
  return (
    <div className="menu" style={{ width: 260 }}>
      <MenuSearch value={q} onChange={setQ} placeholder="Search or add tags..." onEnter={create} />
      {q.trim() && !exact && (
        <button className="menu-item" onClick={create}>
          <span className="menu-item-label">
            Create tag <strong>{q.trim().toLowerCase()}</strong>
          </span>
        </button>
      )}
      {spaceTags.map((t) => (
        <div key={t.id} className="menu-item tag-row" onClick={() => toggle(t.id)}>
          <TagChip tagId={t.id} />
          <span className="spacer" />
          {selected.includes(t.id) && <Check size={14} className="faint" />}
          <Popover
            placement="right-start"
            content={(close) => (
              <div className="menu" style={{ width: 220 }}>
                <input
                  className="input"
                  defaultValue={t.name}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updateTag(t.id, { name: (e.target as HTMLInputElement).value.trim().toLowerCase() || t.name });
                      close();
                    }
                  }}
                />
                <div className="swatches" style={{ gridTemplateColumns: 'repeat(7, 20px)', margin: '10px 4px' }}>
                  {PALETTE.map((c) => (
                    <button key={c} className="swatch" style={{ background: c }} onClick={() => updateTag(t.id, { color: c })}>
                      {c === t.color && <Check size={12} />}
                    </button>
                  ))}
                </div>
                <button
                  className="menu-item danger"
                  onClick={() => {
                    deleteTag(t.id);
                    close();
                  }}
                >
                  <span className="menu-item-icon">
                    <Trash2 size={14} />
                  </span>
                  Delete tag
                </button>
              </div>
            )}
          >
            <button className="icon-btn xs tag-row-more">
              <MoreHorizontal size={14} />
            </button>
          </Popover>
        </div>
      ))}
      {spaceTags.length === 0 && !q && <div className="menu-empty">No tags yet. Type to create one.</div>}
    </div>
  );
}
